import { supabase } from '../lib/supabase';

// API base URL - automatically detects environment
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

/**
 * Admin API Service
 * All admin operations go through the secure backend API
 */
class AdminApiService {
  /**
   * Get the current user's auth token with retry logic
   */
  async getAuthToken(retryCount = 0) {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        // Check if it's a network error
        const isNetworkError = error.message?.toLowerCase().includes('network') ||
                              error.message?.toLowerCase().includes('fetch') ||
                              error.message?.toLowerCase().includes('failed to fetch') ||
                              error.code === 'NETWORK_ERROR';

        if (isNetworkError && retryCount < 5) {
          // Network error - retry with exponential backoff
          const delay = Math.min(Math.pow(2, retryCount) * 2000, 32000); // 2s, 4s, 8s, 16s, 32s max
          console.warn(`[AdminAPI] Network error getting token, retry ${retryCount + 1}/5 in ${delay}ms:`, error.message);

          await new Promise(resolve => setTimeout(resolve, delay));
          return this.getAuthToken(retryCount + 1);
        }

        // Auth error - don't retry
        console.error('[AdminAPI] Auth error getting token:', error);
        throw new Error('Authentication failed: ' + error.message);
      }

      if (!session) {
        // No session but no error - this is an auth issue, not network
        throw new Error('No active session');
      }

      return session.access_token;
    } catch (error) {
      // Handle unexpected errors
      if (error.message?.includes('No active session') || error.message?.includes('Authentication failed')) {
        throw error; // Re-throw auth errors
      }

      // Treat other errors as network errors and retry
      if (retryCount < 5) {
        const delay = Math.min(Math.pow(2, retryCount) * 2000, 32000);
        console.warn(`[AdminAPI] Unexpected error getting token, retry ${retryCount + 1}/5 in ${delay}ms:`, error.message);

        await new Promise(resolve => setTimeout(resolve, delay));
        return this.getAuthToken(retryCount + 1);
      }

      throw error;
    }
  }

  /**
   * Make an authenticated request to the admin API with retry logic
   */
  async request(endpoint, options = {}, retryCount = 0) {
    try {
      const token = await this.getAuthToken();

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...options.headers
        }
      });

      // Handle 401/403 specifically
      if (response.status === 401 || response.status === 403) {
        console.warn(`[AdminAPI] Auth error (${response.status}) on ${endpoint}`);
        // Try refreshing the session once
        if (retryCount === 0) {
          const { error } = await supabase.auth.refreshSession();
          if (!error) {
            // Retry the request with fresh token
            return this.request(endpoint, options, retryCount + 1);
          }
        }
      }

      const data = await response.json();

      if (!response.ok) {
        // Check if it's a network/server error (5xx)
        if (response.status >= 500 && retryCount < 3) {
          const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
          console.warn(`[AdminAPI] Server error (${response.status}), retry ${retryCount + 1}/3 in ${delay}ms`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return this.request(endpoint, options, retryCount + 1);
        }
        throw new Error(data.error || data.message || 'Request failed');
      }

      return data;
    } catch (error) {
      // Handle network errors
      if (error.message?.toLowerCase().includes('fetch') && retryCount < 3) {
        const delay = Math.pow(2, retryCount) * 1000;
        console.warn(`[AdminAPI] Network error, retry ${retryCount + 1}/3 in ${delay}ms:`, error.message);
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.request(endpoint, options, retryCount + 1);
      }

      console.error(`[AdminAPI] Request to ${endpoint} failed:`, error);
      throw error;
    }
  }

  /**
   * Validate if the current user is an admin
   */
  async validateAdmin() {
    try {
      const data = await this.request('/admin/validate');
      return { isAdmin: data.isAdmin, user: data.user };
    } catch (error) {
      return { isAdmin: false, user: null };
    }
  }

  /**
   * Fetch all users with their roles
   */
  async fetchUsers(page = 1, perPage = 100) {
    try {
      const data = await this.request(`/admin/users?page=${page}&perPage=${perPage}`);
      return {
        data: data.users,
        total: data.total,
        error: null
      };
    } catch (error) {
      return {
        data: null,
        total: 0,
        error: error.message
      };
    }
  }

  /**
   * Update a user's role
   */
  async updateUserRole(userId, newRole) {
    try {
      const data = await this.request(`/admin/users/${userId}/role`, {
        method: 'PUT',
        body: JSON.stringify({ role: newRole })
      });
      return {
        success: true,
        message: data.message,
        user: data.user
      };
    } catch (error) {
      return {
        success: false,
        message: error.message
      };
    }
  }

  /**
   * Fetch dashboard statistics
   */
  async fetchStats() {
    try {
      const data = await this.request('/admin/stats');
      return {
        stats: data.stats,
        recentUsers: data.recentUsers,
        error: null
      };
    } catch (error) {
      return {
        stats: null,
        recentUsers: [],
        error: error.message
      };
    }
  }

  /**
   * Check if the admin API is available
   */
  async checkHealth() {
    try {
      const response = await fetch(`${API_BASE_URL}/health`);
      const data = await response.json();
      return data.status === 'OK';
    } catch (error) {
      console.error('Admin API health check failed:', error);
      return false;
    }
  }
}

// Export singleton instance
const adminApi = new AdminApiService();
export default adminApi;